'use client';

import { c as _c } from "react/compiler-runtime";
import { useDisableScrollLock } from '@/hooks/useDisableScrollLock';
import { useEffect, useMemo, useState } from 'react';
export type UseIframeScrollLockOptions = {
  enableFocus?: boolean;
  enableHover?: boolean;
};
export type IframeHandlers = {
  onFocus?: () => void;
  onBlur?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};
export type UseIframeScrollLockReturn = {
  handlers: IframeHandlers;
};

/**
 * Safe and optimized hook for managing scroll lock during iframe interactions
 * Handles missing provider gracefully for maximum compatibility
 *
 * @param {UseIframeScrollLockOptions} options - Configuration options
 * @returns {UseIframeScrollLockReturn} Object containing event handlers and interaction state
 */
export const useIframeScrollLock = (t0, setScrollLock) => {
  const $ = _c(18);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    enableFocus: t2,
    enableHover: t3
  } = options;
  const enableFocus = t2 === undefined ? true : t2;
  const enableHover = t3 === undefined ? true : t3;
  const [isIframeInteracted, setIsIframeInteracted] = useState(false);
  const disableScrollLock = useDisableScrollLock();
  let t4;
  let t5;
  if ($[2] !== disableScrollLock || $[3] !== isIframeInteracted || $[4] !== setScrollLock) {
    t4 = () => {
      if (disableScrollLock) {
        return;
      }
      if (setScrollLock) {
        setScrollLock(isIframeInteracted);
        return () => {
          setScrollLock(false);
        };
      }
    };
    t5 = [isIframeInteracted, setScrollLock, disableScrollLock];
    $[2] = disableScrollLock;
    $[3] = isIframeInteracted;
    $[4] = setScrollLock;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== disableScrollLock || $[8] !== enableFocus || $[9] !== enableHover || $[10] !== setScrollLock) {
    bb0: {
      const result = {};
      if (!setScrollLock || disableScrollLock) {
        t6 = result;
        break bb0;
      }
      if (enableFocus) {
        let t7;
        if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = () => setIsIframeInteracted(true);
          $[12] = t7;
        } else {
          t7 = $[12];
        }
        result.onFocus = t7;
        let t8;
        if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = () => setIsIframeInteracted(false);
          $[13] = t8;
        } else {
          t8 = $[13];
        }
        result.onBlur = t8;
      }
      if (enableHover) {
        let t7;
        if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = () => setIsIframeInteracted(true);
          $[14] = t7;
        } else {
          t7 = $[14];
        }
        result.onMouseEnter = t7;
        let t8;
        if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = () => setIsIframeInteracted(false);
          $[15] = t8;
        } else {
          t8 = $[15];
        }
        result.onMouseLeave = t8;
      }
      t6 = result;
    }
    $[7] = disableScrollLock;
    $[8] = enableFocus;
    $[9] = enableHover;
    $[10] = setScrollLock;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handlers = t6;
  let t7;
  if ($[16] !== handlers) {
    t7 = {
      handlers
    };
    $[16] = handlers;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
};